import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bell, BellRing, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtimeEvents } from "@/hooks/useRealtimeEvents";
import { useDraggable } from "@/hooks/useDraggable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_META = {
  message: { emoji: "💬", color: "border-sky-500/40 bg-sky-500/10 text-sky-200" },
  payment: { emoji: "💵", color: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" },
  appointment: { emoji: "📅", color: "border-amber-500/40 bg-amber-500/10 text-amber-200" },
  reading: { emoji: "🔮", color: "border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-200" },
  profile: { emoji: "🪪", color: "border-orange-500/40 bg-orange-500/10 text-orange-200" },
  system: { emoji: "🔔", color: "border-neutral-600 bg-neutral-700/30 text-neutral-200" },
};

const SOUND_KEY = "ca_user_sound_on";

const playDing = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const make = (freq, delay, dur = 0.18, gain = 0.05) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      g.gain.setValueAtTime(0, now + delay);
      g.gain.linearRampToValueAtTime(gain, now + delay + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, now + delay + dur);
      osc.connect(g).connect(ctx.destination);
      osc.start(now + delay);
      osc.stop(now + delay + dur + 0.02);
    };
    make(660, 0);
    make(880, 0.1);
    setTimeout(() => ctx.close().catch(() => {}), 600);
  } catch { /* noop */ }
};

export const UserNotifBell = () => {
  const { user, authHeader } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [soundOn, setSoundOn] = useState(() => {
    try { return localStorage.getItem(SOUND_KEY) !== "0"; } catch { return true; }
  });
  const lastSoundAtRef = useRef(0);
  const firstLoadRef = useRef(true);

  const fetchNotifs = async () => {
    if (!user) return;
    try {
      const r = await axios.get(`${API}/users/notifications`, { headers: authHeader() });
      const list = r.data || [];
      setItems(list);
      setUnread(list.filter((n) => !n.read).length);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!user) { setItems([]); setUnread(0); return; }
    fetchNotifs();
    // eslint-disable-next-line
  }, [user]);

  useRealtimeEvents({
    notification: () => {
      // chime (skip on initial connection events)
      if (!firstLoadRef.current && soundOn && Date.now() - lastSoundAtRef.current > 500) {
        lastSoundAtRef.current = Date.now();
        playDing();
      }
      firstLoadRef.current = false;
      fetchNotifs();
    },
  });

  const toggleSound = () => {
    setSoundOn((v) => {
      const next = !v;
      try { localStorage.setItem(SOUND_KEY, next ? "1" : "0"); } catch { /* noop */ }
      return next;
    });
  };

  const drag = useDraggable();

  const handleToggleOpen = () => {
    if (drag.dragging) return; // ignore click triggered after a drag
    setOpen((v) => !v);
  };

  const openNotif = async (n) => {
    // mark as read if needed
    if (!n.read) {
      try {
        await axios.patch(`${API}/users/notifications/${n.id}/read`, {}, { headers: authHeader() });
        setItems((arr) => arr.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
        setUnread((u) => Math.max(0, u - 1));
      } catch { /* ignore */ }
    }
    setOpen(false);
    if (n.link) {
      navigate(n.link);
    } else {
      navigate("/mi-cuenta?tab=notifs");
    }
  };

  if (!user || user.role === "admin") return null;

  return (
    <div {...drag} className="fixed top-20 right-4 z-[95] cursor-grab active:cursor-grabbing" data-testid="user-notif-root">
      <div className="relative">
        <button
          onClick={handleToggleOpen}
          data-testid="user-notif-toggle"
          className="relative h-11 w-11 rounded-full bg-black/70 border border-[#d4af37]/40 backdrop-blur-sm flex items-center justify-center text-[#d4af37] hover:scale-105 transition-transform"
          title="Notificaciones"
        >
          {unread > 0 ? <BellRing className="w-5 h-5 animate-bounce" /> : <Bell className="w-5 h-5" />}
          {unread > 0 && (
            <>
              <span data-testid="user-notif-badge" className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center animate-pulse">
                {unread > 99 ? "99+" : unread}
              </span>
              <span className="absolute inset-0 rounded-full border-2 border-[#d4af37] animate-ping opacity-60 pointer-events-none" />
            </>
          )}
        </button>

        {open && (
          <div data-testid="user-notif-panel" className="absolute right-0 mt-2 w-80 max-h-[28rem] overflow-y-auto glass-dark border border-[#d4af37]/30 rounded-2xl p-3 shadow-2xl">
            <div className="flex items-center justify-between mb-2 px-1">
              <span className="text-xs uppercase tracking-widest text-[#d4af37]">Mis notificaciones</span>
              <div className="flex items-center gap-1">
                <button
                  data-testid="user-notif-sound-toggle"
                  onClick={toggleSound}
                  className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${soundOn ? "border-emerald-500/40 text-emerald-300" : "border-neutral-600 text-neutral-400"}`}
                  title="Activar/Silenciar sonido"
                >
                  {soundOn ? "🔊" : "🔇"}
                </button>
                <button onClick={() => setOpen(false)} className="text-neutral-400 hover:text-white"><X className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            {items.length === 0 ? (
              <p className="text-xs text-neutral-500 px-2 py-6 text-center">Sin notificaciones</p>
            ) : (
              <ul className="space-y-1.5">
                {items.slice(0, 30).map((n) => {
                  const meta = CATEGORY_META[n.category] || CATEGORY_META.system;
                  return (
                    <li key={n.id}>
                      <button
                        data-testid={`user-notif-item-${n.id}`}
                        onClick={() => openNotif(n)}
                        className={`w-full text-left bg-black/40 hover:bg-black/60 border ${!n.read ? "border-[#d4af37]/40" : "border-white/5"} rounded-lg px-2.5 py-2 transition`}
                      >
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] uppercase tracking-widest border ${meta.color}`}>
                            {meta.emoji} {n.category || "sistema"}
                          </span>
                          {!n.read && <span className="w-2 h-2 rounded-full bg-[#d4af37] animate-pulse" />}
                          <span className="ml-auto text-[10px] text-neutral-500 font-mono">{new Date(n.created_at).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                        </div>
                        <div className="text-xs text-white font-semibold truncate">{n.title}</div>
                        <div className="text-[11px] text-neutral-300 line-clamp-2">{n.body}</div>
                        {n.link && <div className="text-[10px] text-[#d4af37] mt-1">Toca para abrir →</div>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
